// src/components/Teams/JoinTeam.js
import React, { useEffect, useState, useCallback } from "react";
import {
  Container,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Box,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Alert,
  IconButton
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { collection, getDocs, updateDoc, doc, arrayUnion, arrayRemove } from "firebase/firestore";
import { useAuth } from "../../context/AuthContext";
import { db } from "../../firebase-config";

const JoinTeam = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [availableTeams, setAvailableTeams] = useState([]);
  const [pendingTeams, setPendingTeams] = useState([]);
  const [refresh, setRefresh] = useState(false);

  const fetchTeams = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const teamsSnap = await getDocs(collection(db, "teams"));
      const available = [];
      const pending = [];
      teamsSnap.forEach((docSnap) => {
        const teamData = { id: docSnap.id, ...docSnap.data() };
        // Skip teams where the user is already a member.
        if (teamData.members && teamData.members.includes(user.uid)) {
          return;
        }
        // If the user is already in pendingRequests, add to pending array.
        if (teamData.pendingRequests && teamData.pendingRequests.includes(user.uid)) {
          pending.push(teamData);
        } else {
          available.push(teamData);
        }
      });
      setAvailableTeams(available);
      setPendingTeams(pending);
    } catch (err) {
      console.error("Error fetching teams", err);
      setError("Failed to fetch teams.");
    } finally {
      setLoading(false);
    }
  }, [user]); // Removed refresh from dependencies since it isn't referenced

  useEffect(() => {
    if (user) {
      fetchTeams();
    }
  }, [user, refresh, fetchTeams]);

  const handleRequestJoin = async (teamId) => {
    setError("");
    setMessage("");
    try {
      const teamDocRef = doc(db, "teams", teamId);
      await updateDoc(teamDocRef, {
        pendingRequests: arrayUnion(user.uid)
      });
      setMessage("Join request submitted!");
      setRefresh(!refresh);
    } catch (err) {
      console.error(err);
      setError("Failed to submit join request.");
    }
  };

  const handleCancelRequest = async (teamId) => {
    setError("");
    setMessage("");
    try {
      const teamDocRef = doc(db, "teams", teamId);
      await updateDoc(teamDocRef, {
        pendingRequests: arrayRemove(user.uid)
      });
      setMessage("Join request canceled!");
      setRefresh(!refresh);
    } catch (err) {
      console.error(err);
      setError("Failed to cancel join request.");
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Join Teams
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {message && (
          <Alert
            severity="success"
            action={
              <IconButton color="inherit" size="small" onClick={() => setMessage("")}>
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
            sx={{ mb: 2 }}
          >
            {message}
          </Alert>
        )}
        {loading ? (
          <Box sx={{ textAlign: "center" }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {availableTeams.length === 0 && pendingTeams.length === 0 ? (
              <Typography>No teams are available for you to join.</Typography>
            ) : (
              <>
                {availableTeams.length > 0 && (
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" gutterBottom>
                      Available Teams
                    </Typography>
                    <Table>
                      <TableBody>
                        {availableTeams.map((team) => (
                          <TableRow key={team.id}>
                            <TableCell>{team.teamName}</TableCell>
                            <TableCell align="right">
                              <Button
                                variant="contained"
                                color="secondary"
                                onClick={() => handleRequestJoin(team.id)}
                              >
                                Request to Join
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
                {pendingTeams.length > 0 && (
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" gutterBottom>
                      Pending Join Requests
                    </Typography>
                    <Table>
                      <TableBody>
                        {pendingTeams.map((team) => (
                          <TableRow key={team.id}>
                            <TableCell>{team.teamName}</TableCell>
                            <TableCell align="right">
                              <Button
                                variant="outlined"
                                color="warning"
                                onClick={() => handleCancelRequest(team.id)}
                              >
                                Cancel Request
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </Paper>
    </Container>
  );
};

export default JoinTeam;